import { useEffect, useRef } from 'react';

import { useT } from '../i18n';
import { useToast } from '../lib/toast';
import { useUiStore } from '../stores/ui';
import { Button, Dialog } from './ui';

/**
 * Hai việc, mount một lần trong khung ứng dụng (§4.6):
 *
 * 1. **Cầu nối toast** — rút thông báo từ kho Zustand sang `notification` của
 *    antd, chống trùng theo nội dung để StrictMode không bắn hai lần.
 * 2. **Hộp xác nhận** — dùng cho hành động phá hủy.
 *
 * ⚠️ Khác bản gốc có chủ đích: bản gốc tự dựng overlay `position: fixed`, ghi
 * cứng `bg-white` và dùng class `dark-*`, nên **không chạy đúng ở nền tối**, và
 * nhãn nút luôn là `Hủy`/`Xóa` bất kể hành động là gì (§17.4 mục 2). Ở đây ta
 * dựng trên `Dialog` để có bẫy focus, Esc, và nhãn nút theo đúng hành động:
 * nơi gọi truyền `nhanXacNhan` nhắc lại chính việc sắp xảy ra, nên người dùng
 * trả lời được hộp thoại mà không cần đọc phần thân (§10.2).
 */
export function GlobalUI() {
  const { t } = useT();
  const toast = useUiStore((s) => s.toast);
  const hideToast = useUiStore((s) => s.hideToast);
  const confirm = useUiStore((s) => s.confirm);
  const closeConfirm = useUiStore((s) => s.closeConfirm);
  const { success, error } = useToast();

  const lastShown = useRef<string | null>(null);

  useEffect(() => {
    if (!toast) {
      lastShown.current = null;
      return;
    }
    // Khoá theo nội dung: StrictMode mount hai lần nhưng chỉ hiện một toast.
    const key = `${toast.type}:${toast.msg}`;
    if (lastShown.current === key) return;
    lastShown.current = key;

    if (toast.type === 'error') error(toast.msg);
    else success(toast.msg);
    hideToast();
  }, [toast, success, error, hideToast]);

  return (
    <Dialog
      open={Boolean(confirm)}
      onClose={closeConfirm}
      title={confirm?.title}
      width={400}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={closeConfirm}>
            {confirm?.nhanThoat ?? t('chung.huy')}
          </Button>
          <Button
            variant="destructive"
            size="md"
            onClick={() => {
              confirm?.onOk();
              closeConfirm();
            }}
          >
            {confirm?.nhanXacNhan ?? t('chung.xoa')}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-content-secondary">{confirm?.body}</p>
    </Dialog>
  );
}
